import type { CrosswordClue, CrosswordDirection } from './types';

export type CrosswordSlot = {
  direction: CrosswordDirection;
  start: number;
  cells: number[];
};

export function extractSlots(blocks: readonly boolean[], size: number): CrosswordSlot[] {
  const slots: CrosswordSlot[] = [];
  for (let row = 0; row < size; row += 1) {
    for (let column = 0; column < size; column += 1) {
      const start = row * size + column;
      if (blocks[start]) continue;
      if ((column === 0 || blocks[start - 1]) && column + 1 < size && !blocks[start + 1]) {
        const cells: number[] = [];
        for (let next = column; next < size && !blocks[row * size + next]; next += 1) {
          cells.push(row * size + next);
        }
        slots.push({ direction: 'across', start, cells });
      }
      if ((row === 0 || blocks[start - size]) && row + 1 < size && !blocks[start + size]) {
        const cells: number[] = [];
        for (let next = row; next < size && !blocks[next * size + column]; next += 1) {
          cells.push(next * size + column);
        }
        slots.push({ direction: 'down', start, cells });
      }
    }
  }
  return slots;
}

export function numberSlots(slots: readonly CrosswordSlot[]): Map<number, number> {
  const starts = [...new Set(slots.map((slot) => slot.start))].sort((left, right) => left - right);
  return new Map(starts.map((start, index) => [start, index + 1]));
}

export function clueCells(clue: CrosswordClue, size: number): number[] {
  return Array.from({ length: clue.length }, (_, offset) =>
    clue.direction === 'across' ? clue.start + offset : clue.start + offset * size,
  );
}

export function cluesForCell(clues: readonly CrosswordClue[], size: number, index: number) {
  return clues.filter((clue) => clueCells(clue, size).includes(index));
}

export function firstOpenCell(blocks: readonly boolean[]): number {
  return Math.max(
    0,
    blocks.findIndex((block) => !block),
  );
}

export function crosswordWrongCells(
  entries: readonly string[],
  solution: readonly string[],
): number[] {
  return entries.flatMap((letter, index) =>
    letter !== '' && letter !== solution[index] ? [index] : [],
  );
}

export function isCrosswordSolved(
  entries: readonly string[],
  solution: readonly string[],
): boolean {
  return solution.every((letter, index) => letter === '#' || entries[index] === letter);
}
